import "zod/compile";
import { z } from "zod";
import { ExtensionFieldTypeSchema } from "./schemaExtensions";

/** Read-only generated detail catalog. Custom views and cell catalogs come later. */
const InternalUiElementSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("Card"),
    props: z.strictObject({ title: z.string() }),
    children: z.array(z.string()),
  }),
  z.strictObject({
    type: z.literal("Stack"),
    props: z.strictObject({}),
    children: z.array(z.string()),
  }),
  z.strictObject({
    type: z.literal("Field"),
    props: z.strictObject({
      label: z.string(),
      fieldType: ExtensionFieldTypeSchema,
      value: z.strictObject({ $state: z.string().regex(/^\/values\/[^/]+$/) }),
    }),
    children: z.tuple([]),
  }),
]);

const InternalUiShapeSchema = z.strictObject({
  root: z.string(),
  elements: z.record(z.string(), InternalUiElementSchema),
});
type InternalUiShape = z.infer<typeof InternalUiShapeSchema>;
type InternalUiElement = z.infer<typeof InternalUiElementSchema>;

function completeFieldGraph(
  rootId: string,
  stackId: string,
  stack: InternalUiElement | undefined,
  elements: Map<string, InternalUiElement>,
) {
  if (stack?.type !== "Stack") return false;
  return (
    stack.children.every((id) => elements.get(id)?.type === "Field") &&
    new Set([rootId, stackId, ...stack.children]).size === elements.size &&
    elements.size === stack.children.length + 2
  );
}
function generatedGraph(spec: InternalUiShape) {
  const elements = new Map(Object.entries(spec.elements));
  const root = elements.get(spec.root);
  if (root?.type !== "Card") return false;
  if (root.children.length !== 1) return false;
  const stackId = root.children[0];
  return completeFieldGraph(
    spec.root,
    stackId,
    elements.get(stackId),
    elements,
  );
}

/** Separate from email specs; only a Card -> Stack -> Fields graph is valid. */
export const InternalUiSpecSchema = InternalUiShapeSchema.superRefine(
  (spec, ctx) => {
    if (
      !generatedGraph(spec) ||
      Object.keys(spec.elements).length > 1002 ||
      JSON.stringify(spec).length > 262144
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid generated detail graph or size",
      });
    }
  },
);
export type InternalUiSpec = z.infer<typeof InternalUiSpecSchema>;
